export const STEP_IDS = {
  INTENT: 'intent',
  CATEGORY: 'category',
  ROLE: 'role',
  SKILLS: 'skills',
  CAPACITY: 'capacity',
  TYPE: 'type',
  DURATION: 'duration',
  EXPERIENCE: 'experience',
  LIKELIHOOD: 'likelihood',
  UNPAID_WORK: 'unpaid-work',
  LOCATIONS: 'locations',
  TITLE_AND_DESCRIPTION: 'title-and-description',
  DEADLINE: 'deadline',
  EXAMPLES: 'examples',
  BUDGET: 'budget',
  ABOUT_YOU: 'about-you',
  EMAIL: 'email',
};

export const STEP_PATHS = {
  [STEP_IDS.INTENT]: '/post/intent',
  [STEP_IDS.CATEGORY]: '/post/category',
  [STEP_IDS.ROLE]: '/post/role',
  [STEP_IDS.SKILLS]: '/post/skills',
  [STEP_IDS.CAPACITY]: '/post/capacity',
  [STEP_IDS.TYPE]: '/post/type',
  [STEP_IDS.DURATION]: '/post/duration',
  [STEP_IDS.EXPERIENCE]: '/post/experience',
  [STEP_IDS.LIKELIHOOD]: '/post/likelihood',
  [STEP_IDS.UNPAID_WORK]: '/post/unpaid-work',
  [STEP_IDS.LOCATIONS]: '/post/locations',
  [STEP_IDS.TITLE_AND_DESCRIPTION]: '/post/title-and-description',
  [STEP_IDS.DEADLINE]: '/post/deadline',
  [STEP_IDS.EXAMPLES]: '/post/examples',
  [STEP_IDS.BUDGET]: '/post/budget',
  [STEP_IDS.ABOUT_YOU]: '/post/about-you',
  [STEP_IDS.EMAIL]: '/post/email',
};

/** Post-auth / logged-in handoff (outside the wizard step list). */
export const POST_PENDING_PATH = '/post/pending';

/** Canonical main path for progress (excludes unpaid-work dead-end). */
export const MAIN_PATH_STEP_IDS = [
  STEP_IDS.INTENT,
  STEP_IDS.CATEGORY,
  STEP_IDS.ROLE,
  STEP_IDS.SKILLS,
  STEP_IDS.CAPACITY,
  STEP_IDS.TYPE,
  STEP_IDS.DURATION,
  STEP_IDS.EXPERIENCE,
  STEP_IDS.LIKELIHOOD,
  STEP_IDS.LOCATIONS,
  STEP_IDS.TITLE_AND_DESCRIPTION,
  STEP_IDS.DEADLINE,
  STEP_IDS.EXAMPLES,
  STEP_IDS.BUDGET,
  STEP_IDS.ABOUT_YOU,
  STEP_IDS.EMAIL,
];

const PATH_TO_STEP_ID = Object.keys(STEP_PATHS).reduce((acc, stepId) => {
  acc[STEP_PATHS[stepId]] = stepId;
  return acc;
}, {});

export const getStepIdFromPath = (pathname) =>
  PATH_TO_STEP_ID[pathname] || null;

export const getPathForStepId = (stepId) => STEP_PATHS[stepId] || null;

const JOB_TYPE_INTENTS = ['post_a_job', 'describe_project'];

/**
 * Rebuild the visited stack after browser Back / in-app Back so both stay aligned.
 * Truncate to the last occurrence of currentStepId, then drop that step.
 * If currentStepId is missing, fall back to the previous main-path step.
 *
 * @param {string[]} visitedStepIds
 * @param {string} currentStepId
 * @returns {{ previousStepId: string, nextVisited: string[] }}
 */
export const resolveBackFromVisited = (visitedStepIds, currentStepId) => {
  const visited = Array.isArray(visitedStepIds) ? visitedStepIds.slice() : [];
  const lastIndex = visited.lastIndexOf(currentStepId);

  if (lastIndex >= 0) {
    const nextVisited = visited.slice(0, lastIndex);
    const previousStepId =
      nextVisited[nextVisited.length - 1] || STEP_IDS.INTENT;
    return {
      previousStepId,
      nextVisited: nextVisited.length > 0 ? nextVisited : [STEP_IDS.INTENT],
    };
  }

  const mainIndex = MAIN_PATH_STEP_IDS.indexOf(currentStepId);
  const previousStepId =
    mainIndex > 0 ? MAIN_PATH_STEP_IDS[mainIndex - 1] : STEP_IDS.INTENT;
  return {
    previousStepId,
    nextVisited: [STEP_IDS.INTENT, previousStepId].filter(
      (stepId, index, all) => all.indexOf(stepId) === index
    ),
  };
};

/**
 * Trim visited stack to the current step (browser POP). Keeps the step.
 *
 * @param {string[]} visitedStepIds
 * @param {string} currentStepId
 * @returns {string[]}
 */
export const trimVisitedToStep = (visitedStepIds, currentStepId) => {
  const visited = Array.isArray(visitedStepIds) ? visitedStepIds.slice() : [];
  const lastIndex = visited.lastIndexOf(currentStepId);
  if (lastIndex < 0) {
    // Browser forward / deep-link: keep history and record the new step.
    if (!currentStepId) {
      return visited.length > 0 ? visited : [STEP_IDS.INTENT];
    }
    const base = visited.length > 0 ? visited : [STEP_IDS.INTENT];
    if (base[base.length - 1] === currentStepId) {
      return base;
    }
    return [...base, currentStepId];
  }
  const trimmed = visited.slice(0, lastIndex + 1);
  return trimmed.length > 0 ? trimmed : [STEP_IDS.INTENT];
};

/**
 * Role/skills shortcuts apply only for URL prefills, and only while the
 * skipped steps have never been visited (TP-299 + post-back Next).
 *
 * @param {string[]} prefilledKeys
 * @param {string[]} visitedStepIds
 * @param {Object} answers
 * @returns {{ skipForSkills: boolean, skipForRole: boolean }}
 */
const resolvePrefillSkip = (
  prefilledKeys = [],
  visitedStepIds = [],
  answers = {}
) => {
  const prefilled = new Set(prefilledKeys);
  const visited = new Set(visitedStepIds);
  const hasSkills =
    prefilled.has('skills') && Boolean((answers.skills || '').trim());
  const hasRole = prefilled.has('role') && Boolean((answers.role || '').trim());
  const canSkipCategory = !visited.has(STEP_IDS.CATEGORY);
  const canSkipRole = !visited.has(STEP_IDS.ROLE);
  const canSkipSkills = !visited.has(STEP_IDS.SKILLS);

  return {
    skipForSkills: hasSkills && canSkipCategory && canSkipRole && canSkipSkills,
    skipForRole: hasRole && canSkipCategory && canSkipRole,
  };
};

/**
 * Pure forward navigation. Skip and Next use the same next step except
 * Likelihood unpaid → unpaid-work vs locations.
 * Type (and duration after it) only for post_a_job / describe_project.
 * Browse talent also skips likelihood, and deadline / examples / budget.
 * Contract work types skip duration.
 *
 * @param {string} currentStepId
 * @param {Object} [answers]
 * @param {{ prefilledKeys?: string[], visitedStepIds?: string[] }} [options]
 */
export const getNextStepId = (currentStepId, answers = {}, options = {}) => {
  const isBrowseIntent = answers.intent === 'browse_talent';
  const showsTypeStep = JOB_TYPE_INTENTS.includes(answers.intent);
  const isContractType =
    answers.type === 'part_time_contract' ||
    answers.type === 'full_time_contract';
  const { skipForSkills, skipForRole } = resolvePrefillSkip(
    options.prefilledKeys,
    options.visitedStepIds,
    answers
  );

  switch (currentStepId) {
    case STEP_IDS.INTENT: {
      if (skipForSkills) {
        return STEP_IDS.CAPACITY;
      }
      if (skipForRole) {
        return STEP_IDS.SKILLS;
      }
      return STEP_IDS.CATEGORY;
    }
    case STEP_IDS.CATEGORY: {
      if (skipForSkills) {
        return STEP_IDS.CAPACITY;
      }
      if (skipForRole) {
        return STEP_IDS.SKILLS;
      }
      return STEP_IDS.ROLE;
    }
    case STEP_IDS.ROLE: {
      if (skipForSkills) {
        return STEP_IDS.CAPACITY;
      }
      return STEP_IDS.SKILLS;
    }
    case STEP_IDS.SKILLS:
      return STEP_IDS.CAPACITY;
    case STEP_IDS.CAPACITY:
      return showsTypeStep ? STEP_IDS.TYPE : STEP_IDS.EXPERIENCE;
    case STEP_IDS.TYPE:
      return isContractType ? STEP_IDS.EXPERIENCE : STEP_IDS.DURATION;
    case STEP_IDS.DURATION:
      return STEP_IDS.EXPERIENCE;
    case STEP_IDS.EXPERIENCE:
      return isBrowseIntent ? STEP_IDS.LOCATIONS : STEP_IDS.LIKELIHOOD;
    case STEP_IDS.LIKELIHOOD:
      if (answers.likelihood === 'unpaid_collaboration') {
        return STEP_IDS.UNPAID_WORK;
      }
      return STEP_IDS.LOCATIONS;
    case STEP_IDS.UNPAID_WORK:
      return null;
    case STEP_IDS.LOCATIONS:
      return STEP_IDS.TITLE_AND_DESCRIPTION;
    case STEP_IDS.TITLE_AND_DESCRIPTION:
      return isBrowseIntent ? STEP_IDS.ABOUT_YOU : STEP_IDS.DEADLINE;
    case STEP_IDS.DEADLINE:
      return STEP_IDS.EXAMPLES;
    case STEP_IDS.EXAMPLES:
      return STEP_IDS.BUDGET;
    case STEP_IDS.BUDGET:
      return STEP_IDS.ABOUT_YOU;
    case STEP_IDS.ABOUT_YOU:
      return STEP_IDS.EMAIL;
    case STEP_IDS.EMAIL:
      return null;
    default:
      return null;
  }
};

/** Skip always continues the main path (never unpaid-work). */
export const getSkipStepId = (currentStepId, answers = {}, options = {}) => {
  if (currentStepId === STEP_IDS.LIKELIHOOD) {
    return STEP_IDS.LOCATIONS;
  }
  return getNextStepId(currentStepId, answers, options);
};

export const getProgressFraction = (currentStepId) => {
  if (!currentStepId || currentStepId === STEP_IDS.UNPAID_WORK) {
    const likelihoodIndex = MAIN_PATH_STEP_IDS.indexOf(STEP_IDS.LIKELIHOOD);
    return likelihoodIndex / (MAIN_PATH_STEP_IDS.length - 1);
  }

  const index = MAIN_PATH_STEP_IDS.indexOf(currentStepId);
  if (index < 0) {
    return 0;
  }

  return index / (MAIN_PATH_STEP_IDS.length - 1);
};
